import type { HttpClient } from '@wix/cli-http-client';
import type { Options } from 'p-retry';
import pRetry from 'p-retry';
import { createOverride } from '@wix/ambassador-cli-site-override-service-v1-site-override/http';
import { isHttpError, isHttpApplicationError } from '@wix/cli-http-client';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import type { ComponentOverride } from '@wix/ambassador-cli-site-override-service-v1-site-override/types';
import { createOverrideSchema } from './schemas.js';

export type ComponentsForSiteOverride = Record<string, ComponentOverride>;

export class SiteOverrideClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    const { minRetryTimeout } = getTestOverrides();

    this.retryOptions = {
      retries: 3,
      minTimeout: minRetryTimeout,
      maxTimeout: 3 * 1000,
    };
  }

  public createOverride = async ({
    appId,
    siteId,
    overrides,
    siteOverrideId,
  }: {
    appId: string;
    siteId: string;
    overrides: ComponentsForSiteOverride;
    siteOverrideId?: string;
  }) => {
    try {
      const { data } = await pRetry(
        () =>
          this.httpClient.request(
            createOverride({
              siteOverride: {
                id: siteOverrideId,
                appId,
                siteId,
                overrides,
              },
            })
          ),
        this.retryOptions
      );

      return createOverrideSchema.parse(data).siteOverride;
    } catch (error) {
      if (isHttpError(error) && error.response?.status === 403) {
        if (
          isHttpApplicationError(error) &&
          error.response.data.details.applicationError.code ===
            'PERMISSION_DENIED_FOR_SITE'
        ) {
          throw new CliError({
            code: CliErrorCode.SitePermissionDenied({ siteId }),
            cause: error,
          });
        }

        throw new CliError({
          code: CliErrorCode.AppPermissionDenied({ appId }),
          cause: error,
        });
      }

      throw new CliError({
        code: CliErrorCode.FailedToCreateSiteOverride(),
        cause: error,
      });
    }
  };
}
